import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { AlpanaDivider } from "@/components/tss/Ornaments";
import { CONTACT_EMAIL, SiteFooter } from "@/components/tss/SiteFooter";

const SECTIONS = [
  ["What we collect", [
    "Your name, email address and phone number, entered on the voting form.",
    "Your votes — which puja committee you picked in each category.",
    "Technical signals used only to keep voting fair: a scrambled (hashed) device identifier and a scrambled version of your internet (IP) address. We cannot turn these back into your device or address.",
    "Whether you agreed to receive TSS updates (yes or no).",
  ]],
  ["Why we collect it", [
    "Fair voting: to make sure each person votes only once per category and to spot ballot-stuffing.",
    "A thank-you confirmation after you vote.",
    "Updates and announcements about Sharod Shamman and future community events — only if you ticked the consent box. You can say no and still vote.",
  ]],
  ["Your vote stays anonymous", [
    "Your choices are never shown publicly or linked to your name anywhere on this site. Public results only show totals per committee.",
  ]],
  ["How we store and protect it", [
    "Your information is stored on a secure server and kept confidential.",
    "Only the TSS organizing team can see it, through a password-protected admin console.",
    "We never sell, rent or share your personal information with anyone.",
  ]],
  ["How long we keep it", [
    "We delete all voting data — names, emails, phone numbers and votes — 12 months after voting closes.",
  ]],
  ["Unsubscribing", [
    "Every update email from TSS includes an unsubscribe link, and you can reply STOP to any SMS. You can also email us and we'll remove you right away.",
  ]],
];

export default function PrivacyPage() {
  return (
    <main className="alpana-bg min-h-screen" data-testid="privacy-page">
      <header className="grain bg-maroon px-4 pb-8 pt-6 text-ivory">
        <div className="mx-auto max-w-2xl">
          <Link to="/" data-testid="privacy-back-link" className="inline-flex items-center gap-1 text-sm text-gold-light"><ArrowLeft className="h-4 w-4" /> Back to voting</Link>
          <h1 className="mt-4 font-display text-4xl font-bold">Privacy Policy</h1>
          <p className="mt-1 text-ivory/85">TSS Sharod Shamman 2026 · People's Choice voting</p>
          <AlpanaDivider className="mt-5" />
        </div>
      </header>
      <article className="mx-auto max-w-2xl space-y-5 px-4 py-8">
        <p className="text-[15px] leading-relaxed">
          Your privacy matters to us. This page explains, in plain language, what information the TSS organizing team collects when you vote, why, and how you stay in control of it.
        </p>
        {SECTIONS.map(([title, items]) => (
          <section key={title} className="rounded-2xl border border-gold/40 bg-white p-5" data-testid={`privacy-section-${title.split(" ")[0].toLowerCase()}`}>
            <h2 className="font-display text-xl font-bold text-maroon">{title}</h2>
            <ul className="mt-2 list-disc space-y-1.5 pl-5 text-[15px] leading-relaxed">
              {items.map((t) => <li key={t}>{t}</li>)}
            </ul>
          </section>
        ))}
        <section className="rounded-2xl border-2 border-maroon bg-white p-5" data-testid="privacy-section-contact">
          <h2 className="font-display text-xl font-bold text-maroon">See, correct or delete your data</h2>
          <p className="mt-2 text-[15px] leading-relaxed">
            To ask what we hold about you, correct it, or have it deleted, email the TSS organizing team at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="privacy-contact-email" className="font-semibold text-maroon underline">{CONTACT_EMAIL}</a>. We'll respond promptly.
          </p>
        </section>
      </article>
      <SiteFooter />
    </main>
  );
}
